import { Message } from '@/types'

type MessageHandler = (message: Message) => void
type TypingHandler = (userId: number, isTyping: boolean) => void
type ReadHandler = (messageId: number, userId: number) => void
type ActionChoiceHandler = (message: string) => void  // T050: Handler for action choice events

export class WebSocketService {
  private ws: WebSocket | null = null
  private messageHandlers: Set<MessageHandler> = new Set()
  private typingHandlers: Set<TypingHandler> = new Set()
  private readHandlers: Set<ReadHandler> = new Set()
  private actionChoiceHandlers: Set<ActionChoiceHandler> = new Set()  // T050: Action choice handlers
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  connect(conversationId: number, token: string) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.warn('WebSocket already connected')
      return
    }
    const wsUrl = `${this.getWebSocketUrl()}/ws/conversations/${conversationId}/?token=${token}`

    try {
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('WebSocket connected to conversation', conversationId)
        this.reconnectAttempts = 0
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        console.error('WebSocket connection failed - check backend logs for authentication issues')
      }

      this.ws.onclose = (event) => {
        console.log(`WebSocket disconnected. Close code: ${event.code}, Reason: ${event.reason}`)
        if (event.code === 4001) {
          console.error('Connection closed: Authentication failed - invalid or missing JWT token')
        } else if (event.code === 4003) {
          console.error('Connection closed: Permission denied - user does not have access to this conversation')
        } else if (event.code === 1000) {
          console.log('WebSocket closed normally')
        } else {
          console.warn(`WebSocket closed with code ${event.code}`)
        }
        this.attemptReconnect(conversationId, token)
      }
    } catch (error) {
      console.error('Error connecting WebSocket:', error)
    }
  }

  private getWebSocketUrl(): string {
    const apiUrl = import.meta.env.VITE_WS_URL
    if (apiUrl) {
      return apiUrl
    }

    // Fallback: construct from current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.hostname
    const port = '8000' // Backend port
    return `${protocol}//${host}:${port}`
  }

  private attemptReconnect(conversationId: number, token: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = this.reconnectDelay * this.reconnectAttempts

      console.log(`Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`)

      setTimeout(() => {
        this.connect(conversationId, token)
      }, delay)
    } else {
      console.error('Max reconnect attempts reached')
    }
  }

  private handleMessage(data: any) {
    switch (data.type) {
      case 'chat_message':
      case 'message':
        this.messageHandlers.forEach((handler) => handler(data.message))
        break
      case 'typing':
        this.typingHandlers.forEach((handler) =>
          handler(data.user_id, data.is_typing)
        )
        break
      case 'read':
        this.readHandlers.forEach((handler) =>
          handler(data.message_id, data.user_id)
        )
        break
      case 'action_choice':  // T050: Handle action choice event
        this.actionChoiceHandlers.forEach((handler) =>
          handler(data.message)
        )
        break
      default:
        console.warn('Unknown message type:', data.type)
    }
  }

  sendMessage(content: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected')
      return
    }

    this.ws.send(
      JSON.stringify({
        type: 'message',
        content,
      })
    )
  }

  sendTyping(isTyping: boolean) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    this.ws.send(
      JSON.stringify({
        type: 'typing',
        is_typing: isTyping,
      })
    )
  }

  markAsRead(messageId: number) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    this.ws.send(
      JSON.stringify({
        type: 'read',
        message_id: messageId,
      })
    )
  }

  onMessage(handler: MessageHandler) {
    this.messageHandlers.add(handler)
    return () => {
      this.messageHandlers.delete(handler)
    }
  }

  onTyping(handler: TypingHandler) {
    this.typingHandlers.add(handler)
    return () => {
      this.typingHandlers.delete(handler)
    }
  }

  onRead(handler: ReadHandler) {
    this.readHandlers.add(handler)
    return () => {
      this.readHandlers.delete(handler)
    }
  }

  // T050: Subscribe to action choice events
  onActionChoice(handler: ActionChoiceHandler) {
    this.actionChoiceHandlers.add(handler)
    return () => {
      this.actionChoiceHandlers.delete(handler)
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.messageHandlers.clear()
    this.typingHandlers.clear()
    this.readHandlers.clear()
    this.actionChoiceHandlers.clear()  // T050: Clear action choice handlers
    this.reconnectAttempts = 0
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }
}

// Singleton instance
export const websocketService = new WebSocketService()
